import { useState, useEffect } from 'react';
import { auth, db } from '../../firebase/config';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';

/**
 * useAuth - Hook centralizado de autenticación.
 * Modificado: Almacena el perfil completo de Firestore y calcula nombre e iniciales.
 */
export const useAuth = () => {
    const [user, setUser] = useState(null);
    const [profile, setProfile] = useState(null); // ◄ GUARDAMOS EL PERFIL COMPLETO DE FIRESTORE
    const [role, setRole] = useState(null);
    const [isFullUser, setIsFullUser] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const [loadingAuth, setLoadingAuth] = useState(true);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            if (firebaseUser) {
                setUser(firebaseUser);
                
                let data = null;

                // 1. Buscamos el perfil de colaboradores por UID
                try {
                    const colabRef = doc(db, 'public/data/colaboradores', firebaseUser.uid);
                    const colabSnap = await getDoc(colabRef);
                    if (colabSnap.exists()) {
                        data = colabSnap.data();
                    }
                } catch (error) {
                    console.warn("[useAuth] Nota: El usuario no posee perfil de colaborador:", error.message);
                }

                // 2. Si no existe, buscamos en usuarios generales por UID
                if (!data) {
                    try {
                        const userRef = doc(db, 'public/data/usuarios', firebaseUser.uid);
                        const userSnap = await getDoc(userRef);
                        if (userSnap.exists()) {
                            data = userSnap.data();
                        }
                    } catch (error) {
                        console.warn("[useAuth] Nota: No se pudo leer el documento del usuario por UID:", error.message);
                    }
                }

                if (data) {
                    setProfile(data); // ◄ Guardamos los datos de Firestore

                    const resolvedRole = data.rol || data.perfil || 'Agente';
                    setRole(resolvedRole);

                    const isAdminRole = (val) => 
                        ['ADMINISTRADOR', 'ADMIN', 'FULL'].includes(String(val || '').toUpperCase());

                    const isStrictAdmin = (val) => 
                        ['ADMINISTRADOR', 'ADMIN'].includes(String(val || '').toUpperCase());

                    const hasPermission = 
                        data.accesoFull === true || 
                        isAdminRole(resolvedRole) || 
                        isAdminRole(data.perfil);

                    setIsFullUser(hasPermission);
                    setIsAdmin(isStrictAdmin(resolvedRole) || isStrictAdmin(data.perfil));
                } else {
                    setProfile(null);
                    setRole('Agente');
                    setIsFullUser(false);
                    setIsAdmin(false);
                }
            } else {
                setUser(null);
                setProfile(null);
                setRole(null);
                setIsFullUser(false);
                setIsAdmin(false);
            }
            setLoadingAuth(false);
        });

        return () => unsubscribe();
    }, []);

    // ◄ CÁLCULO CENTRALIZADO DE NOMBRE E INICIALES ◄
    const resolvedName = (
        profile?.nombreCompleto ||
        `${profile?.nombre || ''} ${profile?.apellido || ''}`.trim() ||
        profile?.displayName ||
        user?.displayName ||
        user?.email?.split('@')[0] ||
        'USUARIO'
    ).toUpperCase();

    let resolvedInitials = 'US';
    const partes = resolvedName.trim().split(' ').filter(Boolean);
    if (partes.length >= 2) {
        resolvedInitials = `${partes[0].charAt(0)}${partes[1].charAt(0)}`.toUpperCase();
    } else if (partes.length === 1 && partes[0].length >= 2) {
        resolvedInitials = partes[0].slice(0, 2).toUpperCase();
    }

    return { 
        user, 
        profile,
        userName: resolvedName,       // Ej: "YAMILA TROVATO"
        userInitials: resolvedInitials, // Ej: "YT"
        userEmail: user?.email || '',
        role,
        isFullUser, 
        isAdmin, 
        loadingAuth,
        isAuthenticated: !!user 
    };
};